import React from 'react';
import { PageLayout } from '@/shared/layout';
import { useQsDashboard } from './hooks/useQsDashboard';
import { useCompany } from '@/modules/company/hooks/useCompany';
import { KPICard, Card, Button, Table, Badge, Alert } from '@/components/ui';
import { useNavigate } from 'react-router-dom';

export default function QsDashboardPage() {
  const navigate = useNavigate();
  const { company } = useCompany();
  const { stats, isLoading } = useQsDashboard(company?.id || '');

  const formatCurrency = (val?: number) => {
    if (val === undefined) return '---';
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(val);
  };

  return (
    <PageLayout
      title="Tổng quan QS & Tài chính"
      description="Quản lý khối lượng (QS), biến động chi phí (Variations) và thanh toán dự án"
      breadcrumbs={[{ label: 'Thi công' }, { label: 'QS & Tài chính' }]}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-download-2-line">Báo cáo tài chính</Button>
          <Button variant="primary" icon="ri-add-line" onClick={() => navigate('/construction/payments/create')}>Tạo đề nghị thanh toán</Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          title="Giá trị hợp đồng"
          value={formatCurrency(stats?.totalContractValue)}
          icon="ri-bank-card-line"
          variant="primary"
          isLoading={isLoading}
        />
        <KPICard
          title="Chi phí thực tế"
          value={formatCurrency(stats?.totalActualCost)}
          icon="ri-money-dollar-circle-line"
          variant="info"
          isLoading={isLoading}
        />
        <KPICard
          title="Đã thanh toán"
          value={formatCurrency(stats?.totalPaidAmount)}
          icon="ri-checkbox-circle-line"
          variant="success"
          isLoading={isLoading}
        />
        <KPICard
          title="Phát sinh (VO)"
          value={formatCurrency(stats?.variationAmount)}
          icon="ri-exchange-funds-line"
          variant="warning"
          isLoading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
              <i className="ri-bar-chart-box-line text-os-primary" />
              Tiến độ giải ngân & Tài chính
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-xs font-bold text-slate-500 ">Tiến độ thi công</span>
                    <span className="text-xs font-black text-os-primary">65%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-os-primary rounded-full" style={{ width: '65%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-xs font-bold text-slate-500 ">Tỉ lệ giải ngân</span>
                    <span className="text-xs font-black text-emerald-500">58%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: '58%' }} />
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                <p className="text-[10px] text-slate-400 font-bold  mb-4  tracking-wider">Tình trạng ngân sách</p>
                <div className="flex items-end gap-2 mb-1">
                  <span className="text-2xl font-black text-slate-900">82.4%</span>
                  <span className="text-xs font-bold text-emerald-500 mb-1 flex items-center">
                    <i className="ri-arrow-down-line" />
                    Trong tầm kiểm soát
                  </span>
                </div>
                <p className="text-xs text-slate-500">Ngân sách còn lại: {formatCurrency(450000000)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
             <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <i className="ri-history-line text-slate-400" />
                  Lịch sử thanh toán gần đây
                </h3>
                <Button variant="ghost" size="sm">Xem tất cả</Button>
             </div>
             <Table
                columns={[
                  { key: 'code', title: 'Mã đợt', width: '100px' },
                  { key: 'date', title: 'Ngày yêu cầu' },
                  { key: 'amount', title: 'Giá trị (VND)', align: 'right' },
                  { key: 'status', title: 'Trạng thái', width: '120px' },
                ]}
                data={[
                  { id: '1', code: 'PC-004', date: '20-04-2024', amount: 150000000, status: 'approved' },
                  { id: '2', code: 'PC-003', date: '05-04-2024', amount: 85000000, status: 'paid' },
                  { id: '3', code: 'VO-002', date: '28-03-2024', amount: 45000000, status: 'approved' },
                ]}
                renderCell={(row, key) => {
                  if (key === 'code') return <span className="text-xs font-bold text-os-primary">{row.code}</span>;
                  if (key === 'date') return <span className="text-xs text-slate-500">{row.date}</span>;
                  if (key === 'amount') return <span className="text-xs font-black text-slate-900">{formatCurrency(row.amount)}</span>;
                  if (key === 'status') {
                    const colors: any = { approved: 'bg-emerald-100 text-emerald-600', paid: 'bg-blue-100 text-blue-600' };
                    return <Badge className={`${colors[row.status]} border-transparent text-[10px] font-bold`}>{row.status.toUpperCase()}</Badge>;
                  }
                  return null;
                }}
             />
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-notification-badge-line text-slate-400" />
              Cần xử lý ngay
            </h4>
            <div className="space-y-3">
              <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                  <i className="ri-file-warning-line" />
                </div>
                <div>
                  <p className="text-xs font-bold text-amber-900">2 Payment Claims chờ duyệt</p>
                  <p className="text-[10px] text-amber-700/70">Tổng giá trị: {formatCurrency(210000000)}</p>
                  <Button variant="ghost" size="xs" className="mt-2 text-amber-700 p-0 hover:bg-transparent">Kiểm tra khối lượng ngay →</Button>
                </div>
              </div>

              <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 shrink-0">
                  <i className="ri-error-warning-line" />
                </div>
                <div>
                  <p className="text-xs font-bold text-rose-900">Vượt khối lượng BOQ</p>
                  <p className="text-[10px] text-rose-700/70">Hạng mục: Cây bóng mát (120%)</p>
                  <Button variant="ghost" size="xs" className="mt-2 text-rose-700 p-0 hover:bg-transparent">Lập Variation Order →</Button>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-slate-900 text-white border-none shadow-xl shadow-slate-200">
            <h4 className="font-bold mb-4 flex items-center gap-2 opacity-80">
              <i className="ri-calculator-line" />
              Phân bổ chi phí
            </h4>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-[10px] mb-1 font-bold opacity-60">
                  <span>VẬT TƯ</span>
                  <span>45%</span>
                </div>
                <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-os-primary" style={{ width: '45%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[10px] mb-1 font-bold opacity-60">
                  <span>NHÂN CÔNG</span>
                  <span>30%</span>
                </div>
                <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-400" style={{ width: '30%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[10px] mb-1 font-bold opacity-60">
                  <span>MÁY MÓC / KHÁC</span>
                  <span>25%</span>
                </div>
                <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-400" style={{ width: '25%' }} />
                </div>
              </div>
            </div>
            <Button variant="primary" className="w-full mt-6 bg-white text-slate-900 hover:bg-white/90 border-none">Phân tích chi tiết</Button>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
}
